import React, { useState, useRef, useEffect } from "react";
import CrmCard from "./CrmCard";
import { FiPlus, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiLoader } from "react-icons/fi";


const CrmColumn = ({
    column,
    deals,
    onDrop,
    onDragStart,
    onCardClick,
    onAddClick,
    onSendMessage,
    selectedLeadIds,
    onToggleLeadSelection,
    onToggleColumnSelection,
    stages,
    onStageChange,
    members,
    onAssigneeChange,
    // Pagination props
    pagination,
    onPageChange,
    isLoading,
}) => {
    const [isOver, setIsOver] = useState(false);
    const [pageInputValue, setPageInputValue] = useState("");
    const [showPageInput, setShowPageInput] = useState(false);

    // Ref para checkbox - evita callback ref que pode causar conflitos DOM
    const checkboxRef = useRef(null);

    // Pagination info
    const currentPage = pagination?.page || 1;
    const totalPages = pagination?.pages || 1;
    const totalItems = pagination?.total || deals.length;
    const perPage = pagination?.perPage || 100;

    // Calculate range of items being shown
    const startItem = totalItems > 0 ? (currentPage - 1) * perPage + 1 : 0;
    const endItem = Math.min(currentPage * perPage, totalItems);

    const handlePrevPage = () => {
        if (currentPage > 1 && onPageChange) {
            onPageChange(column.id, currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages && onPageChange) {
            onPageChange(column.id, currentPage + 1);
        }
    };

    const handleFirstPage = () => {
        if (currentPage > 1 && onPageChange) {
            onPageChange(column.id, 1);
        }
    };

    const handleLastPage = () => {
        if (currentPage < totalPages && onPageChange) {
            onPageChange(column.id, totalPages);
        }
    };

    const handlePageInputChange = (e) => {
        const value = e.target.value;
        // Only allow numbers
        if (value === "" || /^\d+$/.test(value)) {
            setPageInputValue(value);
        }
    };

    const handlePageInputSubmit = (e) => {
        e.preventDefault();
        const targetPage = parseInt(pageInputValue, 10);
        if (targetPage && targetPage >= 1 && targetPage <= totalPages && targetPage !== currentPage) {
            onPageChange(column.id, targetPage);
        }
        setPageInputValue("");
        setShowPageInput(false);
    };

    const handlePageInputBlur = () => {
        setPageInputValue("");
        setShowPageInput(false);
    };

    const handlePageInputKeyDown = (e) => {
        if (e.key === "Escape") {
            setPageInputValue("");
            setShowPageInput(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "move";
        setIsOver(true);
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;

        if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
            setIsOver(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOver(false);
        const leadId = e.dataTransfer.getData("leadId");
        if (leadId) {
            onDrop(leadId, column.id);
        }
    };

    const dealCount = deals.length;

    const visibleLeadIds = deals.map((d) => d.id);

    const selectedInColumn = visibleLeadIds.filter((id) =>
        selectedLeadIds?.has
            ? selectedLeadIds.has(id)
            : selectedLeadIds.includes?.(id)
    );

    const allVisibleSelected =
        dealCount > 0 && selectedInColumn.length === dealCount;
    const someSelected =
        selectedInColumn.length > 0 && selectedInColumn.length < dealCount;

    // useEffect para controlar indeterminate - mais seguro que callback ref
    // Evita manipulação direta do DOM durante renderização
    useEffect(() => {
        if (checkboxRef.current) {
            checkboxRef.current.indeterminate = someSelected;
        }
    }, [someSelected]);

    return (
        <section
            className={`crm-column ${isOver ? "crm-column--drop-over" : ""}`}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <header className="crm-column-header">
                <div className="crm-column-title-wrapper">
                    <span
                        className="crm-column-accent"
                        style={{ background: column.color }}
                    />
                    <div>
                        <h3 className="crm-column-title">{column.name}</h3>
                        <p className="crm-column-subtitle">
                            {totalItems} lead{totalItems !== 1 && "s"}
                        </p>
                    </div>
                </div>

                <div className="crm-column-header-actions">
                    <input
                        type="checkbox"
                        className="crm-column-select-checkbox"
                        checked={allVisibleSelected}
                        ref={checkboxRef}
                        onChange={(e) => {
                            e.stopPropagation();
                            if (onToggleColumnSelection) {
                                onToggleColumnSelection(column.id, visibleLeadIds);
                            }
                        }}
                    />
                    <button
                        type="button"
                        className="crm-chip-button"
                        onClick={() => onAddClick(column.id)}
                    >
                        <FiPlus />
                        <span>Novo</span>
                    </button>
                </div>
            </header>

            <div className="crm-column-body">
                {isLoading && (
                    <div className="crm-column-loading">
                        <FiLoader className="crm-loading-spinner" />
                        <span>Carregando...</span>
                    </div>
                )}

                {!isLoading && deals.map((deal) => (
                    <CrmCard
                        key={deal.id}
                        deal={deal}
                        onDragStart={onDragStart}
                        onClick={() => onCardClick(deal)}
                        onSendMessage={onSendMessage}
                        isSelected={
                            selectedLeadIds?.has
                                ? selectedLeadIds.has(deal.id)
                                : selectedLeadIds.includes?.(deal.id)
                        }
                        onToggleSelect={onToggleLeadSelection}
                        stages={stages}
                        onStageChange={onStageChange}
                        members={members}
                        onAssigneeChange={onAssigneeChange}
                    />
                ))}

                {!isLoading && deals.length === 0 && (
                    <div className="crm-column-empty">
                        Arraste um lead para cá ou clique em "Novo".
                    </div>
                )}
            </div>

            {/* Pagination Footer */}
            {totalPages > 1 && (
                <footer className="crm-column-pagination">
                    <div className="crm-pagination-controls">
                        <button
                            type="button"
                            className="crm-pagination-btn crm-pagination-btn-edge"
                            onClick={handleFirstPage}
                            disabled={currentPage <= 1 || isLoading}
                            title="Primeira página"
                        >
                            <FiChevronsLeft />
                        </button>
                        <button
                            type="button"
                            className="crm-pagination-btn"
                            onClick={handlePrevPage}
                            disabled={currentPage <= 1 || isLoading}
                            title="Página anterior"
                        >
                            <FiChevronLeft />
                        </button>

                        <div className="crm-pagination-info">
                            {showPageInput ? (
                                <form onSubmit={handlePageInputSubmit} className="crm-pagination-input-form">
                                    <input
                                        type="text"
                                        className="crm-pagination-input"
                                        value={pageInputValue}
                                        onChange={handlePageInputChange}
                                        onBlur={handlePageInputBlur}
                                        onKeyDown={handlePageInputKeyDown}
                                        placeholder={String(currentPage)}
                                        autoFocus
                                        style={{ width: `${Math.max(2, String(totalPages).length)}ch` }}
                                    />
                                    <span className="crm-pagination-separator">de</span>
                                    <span className="crm-pagination-total-pages">{totalPages}</span>
                                </form>
                            ) : (
                                <button
                                    type="button"
                                    className="crm-pagination-page-btn"
                                    onClick={() => setShowPageInput(true)}
                                    title="Clique para ir para uma página específica"
                                >
                                    <span className="crm-pagination-current">{currentPage}</span>
                                    <span className="crm-pagination-separator">de</span>
                                    <span className="crm-pagination-total-pages">{totalPages}</span>
                                </button>
                            )}
                        </div>

                        <button
                            type="button"
                            className="crm-pagination-btn"
                            onClick={handleNextPage}
                            disabled={currentPage >= totalPages || isLoading}
                            title="Próxima página"
                        >
                            <FiChevronRight />
                        </button>
                        <button
                            type="button"
                            className="crm-pagination-btn crm-pagination-btn-edge"
                            onClick={handleLastPage}
                            disabled={currentPage >= totalPages || isLoading}
                            title="Última página"
                        >
                            <FiChevronsRight />
                        </button>
                    </div>

                    <div className="crm-pagination-range">
                        {startItem}-{endItem} de {totalItems}
                    </div>
                </footer>
            )}
        </section>
    );
};

export default CrmColumn;
